import pluralize from 'pluralize'

import MoneyM from '@/gf/modules/Money'

import useToggle from '@/gf/hooks/useToggle'
import useConfig from '@/gf/hooks/useConfig'

import Card from '@/gf/components/Card'
import Modal from '@/gf/components/Modal'

import { ShippingCost, Money, Maybe, CardProps } from '../../types'

import { NewStoreOrder, CreateQuoteForm } from '../types'

const NewQuotePayment = ({
  newStoreOrders,
  shippingCosts,
  suggestedShippingCost,
  form,
  primaryFooterAction,
  secondaryFooterActions,
}: {
  newStoreOrders: NewStoreOrder[]
  shippingCosts: ShippingCost[]
  suggestedShippingCost?: Maybe<Money>
  form: CreateQuoteForm
} & Pick<CardProps, 'primaryFooterAction' | 'secondaryFooterActions'>) => {
  const config = useConfig()
  const [helpOpen, helpToggler] = useToggle(false)

  const allPickup = newStoreOrders.reduce(
    (acc, newStoreOrder) =>
      acc && !!form.stores.find((s) => s.id === newStoreOrder.store.id)?.pickup,
    true
  )

  const subtotal = newStoreOrders
    .map((nso) => nso.subtotal)
    .reduce(MoneyM.add, MoneyM.fromDecimal(0, 'USD'))

  const shippingSurcharges =
    shippingCosts.length > 0 &&
    shippingCosts
      .map((c) => MoneyM.mult(MoneyM.fromString(c.shippingCost, 'USD'), 1))
      .reduce(MoneyM.add, MoneyM.fromDecimal(0, 'USD'))

  const totalWithoutConvenienceFee = [
    subtotal,
    shippingSurcharges || suggestedShippingCost || MoneyM.fromDecimal(0, 'USD'),
  ].reduce(MoneyM.add)

  const { convenienceFeeRate } = config
  const convenienceFee = MoneyM.mult(totalWithoutConvenienceFee, convenienceFeeRate)
  const total = MoneyM.add(totalWithoutConvenienceFee, convenienceFee)

  const totalItems = newStoreOrders.flatMap((nso) => nso.lineItems).length

  return (
    <>
      {helpOpen && (
        <Modal open={helpOpen} onClose={() => helpToggler.off()} title="Help">
          <div className="mt-2">
            <p className="text-sm text-gray-500">
              This option will allow the buyer to pay with invoice. This is typically reserved for
              enterprise accounts.
            </p>
          </div>
        </Modal>
      )}
      <Card
        title="Payment"
        secondaryFooterActions={secondaryFooterActions}
        primaryFooterAction={primaryFooterAction}
      >
        <Card.Section>
          <Card.SubSection>
            <div className="space-y-2 text-sm text-gray-700">
              <div className="flex justify-between">
                <span className="space-x-4">
                  <span>Subtotal</span>
                  <span>{`${totalItems} ${pluralize('items', totalItems)}`}</span>
                </span>
                <span>{MoneyM.format(subtotal)}</span>
              </div>

              {!allPickup && (
                <div className="flex justify-between">
                  <span>Shipping</span>
                  <span>
                    {shippingCosts.length > 0 && shippingSurcharges
                      ? MoneyM.format(shippingSurcharges)
                      : suggestedShippingCost
                        ? `(Suggested Shipping Amount) ${MoneyM.format(suggestedShippingCost)}`
                        : '(Calculated after creating the order)'}
                  </span>
                </div>
              )}

              {!MoneyM.isZero(convenienceFee) && (
                <div className="flex justify-between">
                  <span>Customer Fee</span>
                  <span>{MoneyM.format(convenienceFee)}</span>
                </div>
              )}
            </div>
          </Card.SubSection>
          <Card.SubSection>
            <div className="flex justify-between text-sm text-gray-700 font-bold">
              <span>Total</span>
              <span>{MoneyM.format(total)}</span>
            </div>
          </Card.SubSection>
        </Card.Section>
      </Card>
    </>
  )
}

export default NewQuotePayment
